import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Calendar from "./Calendar";
import LocationPicker from "./LocationPicker";
import { fetchVehicleActiveRates, fetchVehicleBlockedDates, fetchSeasonalDates } from "../../services/vehicleService";
import { calculateDailyPrices } from "../../services/pricingService";
import { useTranslation } from "../../hooks/useTranslation";
import { MEDIA_URL } from "../../config/constants";
import { trackAddToCart } from "../../services/analyticsService";
import { useVehicles } from "../../context/VehicleContext";

// Discount tiers configuration - applied to base rental only (moved outside component for stability)
const DISCOUNT_TIERS = [
  { nights: 9, rate: 0.10, label: '10%' },
  { nights: 6, rate: 0.05, label: '5%' }
];

// Calculate discount based on number of nights (moved outside component for stability)
const getStayDiscount = (nights) => {
  for (const tier of DISCOUNT_TIERS) {
    if (nights >= tier.nights) {
      return { rate: tier.rate, label: tier.label, minNights: tier.nights };
    }
  }
  return { rate: 0, label: '0%', minNights: 0 };
};

const EnhancedBookingForm = ({ price, priceDescription, vehicleId, vanTitle, vanImage }) => {
  const navigate = useNavigate();
  const { t, getLocalizedPath } = useTranslation('camperDetails');
  const { selectedDates } = useVehicles();

  // Initialize dates from VehicleContext if available (from main page search)
  const initStartDate = selectedDates.pickupDate ? new Date(selectedDates.pickupDate) : null;
  const initEndDate = selectedDates.dropoffDate ? new Date(selectedDates.dropoffDate) : null;

  const [selectedStartDate, setSelectedStartDate] = useState(initStartDate);
  const [selectedEndDate, setSelectedEndDate] = useState(initEndDate);
  const [pickupLocation, setPickupLocation] = useState(null);
  const [dropoffLocation, setDropoffLocation] = useState(null);
  const [pickupTime, setPickupTime] = useState(null);
  const [dropoffTime, setDropoffTime] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [totalPrice, setTotalPrice] = useState(0);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [dailyPrices, setDailyPrices] = useState({});
  const [reservedDates, setReservedDates] = useState([]);
  // Drop-off days that are still free for a same-day pickup.
  const [turnaroundDates, setTurnaroundDates] = useState([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);

  const basePrice = 86.48; // Fallback base daily price

  // Validate vehicleId prop
  if (!vehicleId) {
    console.error('EnhancedBookingForm: vehicleId prop is required');
  }

  // Expand booking ranges into individual dates, splitting them into days the
  // van is unavailable and days it is only free from the afternoon.
  //
  // A booking's end date is its drop-off day. When the van comes back early
  // enough (the backend flags this as pickup_available_on_end_date) it can go
  // straight out again with the next customer, so that date must stay open for
  // a new pickup instead of being blacked out — two bookings, one day.
  const expandDateRanges = (blockedDates) => {
    const reserved = new Set();
    const turnaround = new Set();

    if (!blockedDates || !Array.isArray(blockedDates)) {
      return { reserved: [], turnaround: [] };
    }

    const key = (d) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    blockedDates.forEach(range => {
      if (!range.start_date || !range.end_date) return;

      const startDate = new Date(range.start_date);
      const endDate = new Date(range.end_date);
      const currentDate = new Date(startDate);

      while (currentDate <= endDate) {
        const isEndDate = currentDate.getTime() === endDate.getTime();

        if (isEndDate && range.pickup_available_on_end_date) {
          turnaround.add(key(currentDate));
        } else {
          reserved.add(key(currentDate));
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }
    });

    // A day another booking genuinely occupies always wins over a turnaround
    // hint coming from a different booking.
    return {
      reserved: [...reserved],
      turnaround: [...turnaround].filter((d) => !reserved.has(d)),
    };
  };

  // Fetch vehicle pricing data on component mount
  useEffect(() => {
    if (!vehicleId) return; // Don't fetch if vehicleId is not provided

    const fetchVehiclePricing = async () => {
      try {
        setIsLoadingPrices(true);

        // Fetch seasonal rates from API
        const seasonalRates = await fetchVehicleActiveRates(vehicleId);

        // Fetch seasonal date ranges from API
        const seasonalDates = await fetchSeasonalDates();

        // Debug logging
        if (seasonalDates.length === 0) {
          console.error('⚠️ No seasonal dates received from API - prices will use fallback!');
        }
        if (seasonalRates.length === 0) {
          console.error('⚠️ No seasonal rates received from API!');
        }

        // Calculate daily prices
        const prices = calculateDailyPrices(seasonalRates, seasonalDates);
        console.log(`💰 Daily prices calculated: ${Object.keys(prices).length} dates mapped`);
        setDailyPrices(prices);

        // Fetch real blocked dates from API
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
        const endDate = `${oneYearFromNow.getFullYear()}-${String(oneYearFromNow.getMonth() + 1).padStart(2, '0')}-${String(oneYearFromNow.getDate()).padStart(2, '0')}`;

        const blockedDatesResponse = await fetchVehicleBlockedDates(vehicleId, today, endDate);
        const blockedDateRanges = blockedDatesResponse.blocked_dates || [];

        // Transform date ranges into individual date strings for Calendar component
        const { reserved, turnaround } = expandDateRanges(blockedDateRanges);
        setReservedDates(reserved);
        setTurnaroundDates(turnaround);

        setIsLoadingPrices(false);
      } catch (error) {
        console.error('Error fetching vehicle pricing:', error);
        setIsLoadingPrices(false);
      }
    };

    fetchVehiclePricing();
  }, [vehicleId]);

  // Format date as YYYY-MM-DD string to avoid timezone issues with JSON.stringify
  const formatDateForStorage = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleBookingSubmit = () => {
    if (selectedStartDate && selectedEndDate) {
      // Calculate number of nights
      const timeDiff = selectedEndDate.getTime() - selectedStartDate.getTime();
      const nights = Math.ceil(timeDiff / (1000 * 3600 * 24));

      // Use props for van title and image (fallback to defaults if not provided)
      const bookingVanTitle = vanTitle || "Sun Catchers";
      const bookingVanImage = vanImage || `${MEDIA_URL}/images/placeholders/campervan-detail-1.jpg`;

      // Use the actual calculated average daily rate from priceBreakdown, not the hardcoded fallback
      const actualBasePrice = priceBreakdown?.dailyRate || basePrice;

      // Store comprehensive booking data
      const bookingData = {
        vehicleId,
        vanTitle: bookingVanTitle,
        vanImage: bookingVanImage,
        startDate: formatDateForStorage(selectedStartDate),
        endDate: formatDateForStorage(selectedEndDate),
        pickupLocation,
        dropoffLocation,
        pickupTime,
        dropoffTime,
        basePrice: actualBasePrice,
        nights: nights,
        totalPrice,
        priceBreakdown,
      };
      sessionStorage.setItem("bookingData", JSON.stringify(bookingData));

      // Track add to cart for GA4
      trackAddToCart(bookingData);

      // Navigate to checkout step 1
      navigate(getLocalizedPath("/checkout/step1"));
    } else {
      alert(t('bookingForm.selectDatesAlert'));
    }
  };

  const calculateTotalPrice = useCallback(() => {
    if (!selectedStartDate || !selectedEndDate) {
      setTotalPrice(0);
      setPriceBreakdown(null);
      return;
    }

    const days = Math.ceil(
      (selectedEndDate - selectedStartDate) / (1000 * 60 * 60 * 24),
    );

    // Calculate total using seasonal pricing
    let dailyTotal = 0;
    let averageRate = basePrice;
    
    if (Object.keys(dailyPrices).length > 0) {
      const currentDate = new Date(selectedStartDate);
      let totalRate = 0;
      let dayCount = 0;
      
      while (currentDate < selectedEndDate) {
        const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
        const dayPrice = dailyPrices[dateStr] || basePrice;
        dailyTotal += dayPrice;
        totalRate += dayPrice;
        dayCount++;
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      averageRate = dayCount > 0 ? totalRate / dayCount : basePrice;
    } else {
      dailyTotal = days * basePrice;
    }

    // Calculate stay-length discount (applies to base rental only)
    const discount = getStayDiscount(days);
    const baseDiscount = dailyTotal * discount.rate;
    const discountedBase = dailyTotal - baseDiscount;

    let pickupFee = 0;
    let dropoffFee = 0;
    let timeFees = 0;

    if (pickupLocation) {
      pickupFee = pickupLocation.fee || 0;
    }

    if (dropoffLocation) {
      dropoffFee = dropoffLocation.fee || 0;
    }

    if (pickupTime) {
      timeFees += pickupTime.fee || 0;
    }

    if (dropoffTime) {
      timeFees += dropoffTime.fee || 0;
    }

    // Use discountedBase in total calculation
    const total = discountedBase + pickupFee + dropoffFee + timeFees;

    setTotalPrice(total);
    setPriceBreakdown({
      days,
      dailyRate: averageRate,
      dailyTotal,
      baseDiscount,
      baseDiscountRate: discount.rate,
      discountedBase,
      pickupFee,
      dropoffFee,
      timeFees,
      total,
    });
  }, [selectedStartDate, selectedEndDate, basePrice, dailyPrices, pickupLocation, dropoffLocation, pickupTime, dropoffTime]);

  useEffect(() => {
    calculateTotalPrice();
  }, [calculateTotalPrice]);

  const handleDateSelect = (startDate, endDate) => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    
    // Auto-close calendar when both dates are selected
    if (startDate && endDate) {
      setShowCalendar(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return t('bookingForm.selectDate');
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const isBookingComplete =
    selectedStartDate &&
    selectedEndDate &&
    pickupLocation &&
    dropoffLocation &&
    pickupTime &&
    dropoffTime;

  return (
    <div className="enhanced-booking-details">
      <div className="price-container">
        <div className="price">
          €{totalPrice > 0 ? totalPrice.toFixed(2) : basePrice.toFixed(2)}
        </div>
        <div className="price-description">
          {totalPrice > 0 ? t('bookingForm.total') : t('bookingForm.perNight')}
          {isLoadingPrices && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {t('bookingForm.loadingPrices')}
            </div>
          )}
        </div>
      </div>

      {/* Date Selection */}
      <div className="booking-section">
        <div className="section-title">{t('bookingForm.selectDates')}</div>
        <div
          className="date-display"
          onClick={() => setShowCalendar(!showCalendar)}
        >
          <div className="date-range">
            <div className="date-item">
              <span className="date-label">{t('bookingForm.pickupLabel')}</span>
              <span className="date-value">
                {formatDate(selectedStartDate)}
              </span>
            </div>
            <div className="date-separator">
              →
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21 10H3M16 2V6M8 2V6M7.8 22H16.2C17.8802 22 18.7202 22 19.362 21.673C19.9265 21.3854 20.3854 20.9265 20.673 20.362C21 19.7202 21 18.8802 21 17.2V8.8C21 7.11984 21 6.27976 20.673 5.63803C20.3854 5.07354 19.9265 4.6146 19.362 4.32698C18.7202 4 17.8802 4 16.2 4H7.8C6.11984 4 5.27976 4 4.63803 4.32698C4.07354 4.6146 3.6146 5.07354 3.32698 5.63803C3 6.27976 3 7.11984 3 8.8V17.2C3 18.8802 3 19.7202 3.32698 20.362C3.6146 20.9265 4.07354 21.3854 4.63803 21.673C5.27976 22 6.11984 22 7.8 22Z"
                  stroke="#009B31"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="date-item">
              <span className="date-label">{t('bookingForm.dropoffLabel')}</span>
              <span className="date-value">{formatDate(selectedEndDate)}</span>
            </div>
          </div>
        </div>

        {showCalendar && (
          <div className="calendar-wrapper">
            <Calendar
              selectedStartDate={selectedStartDate}
              selectedEndDate={selectedEndDate}
              onDateSelect={handleDateSelect}
              reservedDates={reservedDates}
              turnaroundDates={turnaroundDates}
              dailyPrices={dailyPrices}
              minStay={2}
            />
          </div>
        )}

        {/* Discount Notification - Always show both tiers */}
        {selectedStartDate && selectedEndDate && (
          <div className="discount-notification">
            <div className="discount-tiers">
              {DISCOUNT_TIERS.slice().reverse().map((tier) => {
                const currentNights = priceBreakdown?.days || 0;
                return (
                  <span
                    key={tier.nights}
                    className={`discount-tier ${currentNights >= tier.nights ? 'qualified' : ''} ${priceBreakdown?.baseDiscountRate === tier.rate ? 'active' : ''}`}
                  >
                    {t('bookingForm.stayDiscount', { nights: tier.nights, discount: tier.label })}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="booking-separator" />

      {/* Location Selection */}
      <div className="booking-section">
        <div className="section-title">{t('bookingForm.pickupDropoff')}</div>
        <div className="location-fields">
          <LocationPicker
            label={t('bookingForm.pickupLocation')}
            type="pickup"
            onLocationSelect={setPickupLocation}
            selectedLocation={pickupLocation?.id}
            selectedTime={pickupTime?.time}
            onTimeSelect={setPickupTime}
            vehicleId={vehicleId}
            selectedDate={selectedStartDate}
          />
          <LocationPicker
            label={t('bookingForm.dropoffLocation')}
            type="dropoff"
            onLocationSelect={setDropoffLocation}
            selectedLocation={dropoffLocation?.id}
            selectedTime={dropoffTime?.time}
            onTimeSelect={setDropoffTime}
            vehicleId={vehicleId}
            selectedDate={selectedEndDate}
          />
        </div>
      </div>

      {/* Price Breakdown */}
      {priceBreakdown && (
        <>
          <div className="booking-separator" />
          <div className="price-breakdown">
            <div className="breakdown-title">{t('bookingForm.priceBreakdown')}</div>
            <div className="breakdown-item">
              <span>
                €{priceBreakdown.dailyRate.toFixed(2)} × {priceBreakdown.days} {t('bookingForm.nights')}
                {Object.keys(dailyPrices).length > 0 && (
                  <small style={{ display: 'block', color: '#666', fontSize: '12px' }}>
                    {t('bookingForm.seasonalPricing')}
                  </small>
                )}
              </span>
              <span>€{priceBreakdown.dailyTotal.toFixed(2)}</span>
            </div>
            {priceBreakdown.baseDiscount > 0 && (
              <div className="breakdown-item discount-item">
                <span className="discount-label">
                  {t('bookingForm.stayLengthDiscount', { percent: Math.round(priceBreakdown.baseDiscountRate * 100) })}
                </span>
                <span className="discount-value">-€{priceBreakdown.baseDiscount.toFixed(2)}</span>
              </div>
            )}
            {priceBreakdown.pickupFee > 0 && (
              <div className="breakdown-item">
                <span>{t('bookingForm.pickupFee')}</span>
                <span>€{priceBreakdown.pickupFee.toFixed(2)}</span>
              </div>
            )}
            {priceBreakdown.dropoffFee > 0 && (
              <div className="breakdown-item">
                <span>{t('bookingForm.dropoffFee')}</span>
                <span>€{priceBreakdown.dropoffFee.toFixed(2)}</span>
              </div>
            )}
            {priceBreakdown.timeFees > 0 && (
              <div className="breakdown-item">
                <span>{t('bookingForm.timeSlotFees')}</span>
                <span>€{priceBreakdown.timeFees.toFixed(2)}</span>
              </div>
            )}
            <div className="breakdown-separator" />
            <div className="breakdown-item total">
              <span>{t('bookingForm.total')}</span>
              <span>€{priceBreakdown.total.toFixed(2)}</span>
            </div>
          </div>
        </>
      )}

      <div
        className="book-now-button"
        style={{
          opacity: isBookingComplete ? 1 : 0.6,
          cursor: isBookingComplete ? "pointer" : "not-allowed",
        }}
        onClick={isBookingComplete ? handleBookingSubmit : undefined}
      >
        <div className="button-state-layer">
          {isBookingComplete ? t('bookingForm.requestToBook') : t('bookingForm.completeSelection')}
        </div>
      </div>

      {!isBookingComplete && (
        <div className="booking-requirements">
          <div className="requirements-title">{t('bookingForm.stillNeeded')}</div>
          <ul className="requirements-list">
            {!selectedStartDate && <li>{t('bookingForm.selectPickupDate')}</li>}
            {!selectedEndDate && <li>{t('bookingForm.selectDropoffDate')}</li>}
            {!pickupLocation && <li>{t('bookingForm.choosePickupLocation')}</li>}
            {!dropoffLocation && <li>{t('bookingForm.chooseDropoffLocation')}</li>}
            {!pickupTime && <li>{t('bookingForm.selectPickupTime')}</li>}
            {!dropoffTime && <li>{t('bookingForm.selectDropoffTime')}</li>}
          </ul>
        </div>
      )}
    </div>
  );
};

export default EnhancedBookingForm;
